import { FC, useContext, useEffect, useState } from 'react';
import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { SupportAgent } from '@mui/icons-material';
import EmailIcon from '@mui/icons-material/Email';
import { NavContext } from '../../context/NavContext';
import Modal from '../common/Modal/Modal';
import ContactUsForm from '../ContactUsForm/ContactUsForm';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import { useTour } from '@reactour/tour';
import useCartOrderContext from '../../hooks/useCartOrder';

interface TopBarProps { }

const TopBar: FC<TopBarProps> = () => {
  const navContextData = useContext(NavContext);
  const { setTab } = useCartOrderContext();
  const [showContactUsModal, setShowContactUsModal] = useState<boolean>(false);
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('tablet'));
  const { steps, currentStep, setCurrentStep, isOpen, setIsOpen } = useTour();

  /*
    Handle switching between inventory tab and order tab
    on cart order page
  */
  useEffect(() => {
    if (isOpen) {
      const currentStepObj = steps[currentStep];
      if (currentStepObj.selector.toString().includes('#Inventory')) {
        setTab('inventory');
      } else {
        setTab('order');
      }
    }
  }, [isOpen, steps, currentStep])

  return (
    <Box id="TopBar"
      sx={(theme) => ({
        position: 'fixed',
        zIndex: 1,
        width: `calc(100vw - ${navContextData?.navExpanded ? '300px' : '65px'})`,
        height: '52px',
        backgroundColor: '#3D4449',
        [theme.breakpoints.down('tablet')]: {
          width: '100%',
          height: '64px'
        }
      })}>
      <Stack sx={(theme) => ({
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingRight: '16px',
        '& .MuiStack-root': {
          '& .MuiButtonBase-root': {
            '& .MuiSvgIcon-root': {
              fontSize: 24,
              color: '#FFF',
              marginRight: '8px',
            }
          }
        },
        [theme.breakpoints.down('tablet')]: {
          paddingRight: 0,
          justifyContent: 'space-between',
          '& .MuiStack-root': {
            width: '100%',
            '& .MuiButtonBase-root': {
              width: '100%',
              '& .MuiSvgIcon-root': {
                fontSize: 32,
                marginRight: 0
              }
            }
          }
        }
      })}>
        {steps.length > 0 &&
          <Stack
            direction="row"
            alignItems="center">
            <IconButton
              onClick={() => { setCurrentStep(0); setIsOpen(true); }}>
              <AnnouncementOutlinedIcon />
              {!isTablet &&
                <Typography
                  fontSize="16px"
                  variant="secondaryFont"
                  color="#FFF">
                  View Tutorial
                </Typography>
              }
            </IconButton>
          </Stack>
        }
        <Stack
          direction="row"
          alignItems="center">
          <IconButton
            onClick={() => { document.location.href = `tel:8005579541` }}>
            <SupportAgent />
            {!isTablet &&
              <Typography
                fontSize="16px"
                variant="secondaryFont"
                color="#FFF">
                (800) 557-9541
              </Typography>
            }
          </IconButton>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          marginRight="12px">
          <IconButton
            onClick={() => { setShowContactUsModal(true) }}>
            <EmailIcon />
            {!isTablet &&
              <Typography
                fontSize="16px"
                variant="secondaryFont"
                color="#FFF">
                Create A Ticket
              </Typography>
            }
          </IconButton>
        </Stack>
      </Stack>
      <ContactUsModal isVisible={showContactUsModal} closeModal={() => { setShowContactUsModal(false) }} />
    </Box>
  );
}

interface ContactUsModalPromptProps {
  closeModal: () => void;
  isVisible: boolean;
}

const ContactUsModal: FC<ContactUsModalPromptProps> = ({ isVisible, closeModal }) => {
  return (
    <>
      {isVisible &&
        <Modal title={"Contact Us"} closeModal={closeModal}>
          <Stack direction="column">
            <ContactUsForm closeModal={closeModal} />
          </Stack>
        </Modal>
      }
    </>
  )
}

export default TopBar;
